// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// Modalidad = evento + equipamiento efectivo. Es la unidad con la que se premia:
// "Powerlifting CLASSIC", "Banca RAW", "Push Pull RAW"... Resultados nunca descarta
// atletas por día, porque un atleta de doble inscripción levanta una sola vez y su
// categoría chica se premia con el resto de esa modalidad, compita el día que
// compita. El filtro por día elige categorías por su día de cierre.

import { entryHasLifted, isEntryNoShow, isEntryPresent } from "./entry";
import { equipmentForEvent, wrapsOnlyAffectSquatEnabled } from "./equipmentForEvent";
import { localizeEquipment, localizeEvent } from "./strings";
import { Entry, Equipment, Event, Language, Sex } from "../types/dataTypes";
import { MeetState } from "../types/stateTypes";

export const ALL_MODALITIES = "";
export const ALL_SEXES = "";
export const ALL_DIVISIONS = "";
export const ALL_DAYS = "";

export type ModalityOption = {
  key: string;
  event: Event;
  equipment: Equipment;
};

export const modalityKey = (event: Event, equipment: Equipment): string => `${event}|${equipment}`;

// Equipamiento con el que compite una entry EN ESE EVENTO: primero la regla de
// vendas (que puede bajar Wraps a Sleeves fuera de la sentadilla) y después los
// combinados generales del meet.
export const effectiveEquipment = (equipment: Equipment, event: Event, meet: MeetState): Equipment => {
  const wrapsOnlyAffectSquat = wrapsOnlyAffectSquatEnabled(meet);
  const forEvent = equipmentForEvent(equipment, event, wrapsOnlyAffectSquat);
  if (meet.combineSleevesAndWraps && forEvent === "Sleeves") return "Wraps";
  if (meet.combineSingleAndMulti && forEvent === "Single-ply") return "Multi-ply";
  return forEvent;
};

// Modalidades presentes en el meet, en el orden en que aparecen las entries.
export const modalitiesInMeet = (entries: ReadonlyArray<Entry>, meet: MeetState): ModalityOption[] => {
  const seen = new Map<string, ModalityOption>();
  for (const entry of entries) {
    for (const event of entry.events) {
      const equipment = effectiveEquipment(entry.equipment, event, meet);
      const key = modalityKey(event, equipment);
      if (!seen.has(key)) seen.set(key, { key, event, equipment });
    }
  }
  return [...seen.values()].sort((a, b) => {
    // Más movimientos primero (SBD, BD, luego los especialistas), después equipamiento.
    if (a.event.length !== b.event.length) return b.event.length - a.event.length;
    if (a.event !== b.event) return a.event.localeCompare(b.event);
    return a.equipment.localeCompare(b.equipment);
  });
};

export const modalityLabel = (modality: ModalityOption, language: Language): string =>
  `${localizeEvent(modality.event, language)} — ${localizeEquipment(modality.equipment, language)}`;

// Sexos presentes, en el orden en que se premia (mujeres primero, como la web).
export const sexesInMeet = (entries: ReadonlyArray<Entry>): Sex[] => {
  const order: Sex[] = ["F", "M", "Mx"];
  const present = new Set(entries.map((entry) => entry.sex));
  return order.filter((sex) => present.has(sex));
};

// Divisiones presentes, en el orden declarado en "Reglas de la competición".
export const divisionsInMeet = (entries: ReadonlyArray<Entry>, meet: MeetState): string[] => {
  const present = new Set<string>();
  for (const entry of entries) {
    for (const division of entry.divisions) present.add(division);
  }
  const declared = meet.divisions.filter((division) => present.has(division));
  const extra = [...present].filter((division) => !meet.divisions.includes(division)).sort();
  return [...declared, ...extra];
};

// Los que todavía tienen que levantar en esta categoría: sin intentos juzgados y
// no marcados como ausentes en Pesaje. Son los que hacen que la categoría NO esté
// cerrada todavía, aunque la tabla se vea completa.
export const pendingEntries = (orderedEntries: ReadonlyArray<Entry>): Entry[] =>
  orderedEntries.filter((entry) => !entryHasLifted(entry) && isEntryPresent(entry));

// Los días en que compite una categoría: los de sus propios atletas, los que la
// tienen como modalidad principal (el primer evento y la primera división de la
// entry, como las arma el bundle). Un doble inscripto que levantó otro día solo le
// presta su resultado. Si la categoría se arma solo con resultados prestados,
// cuentan los días de todos. El ausente de Pesaje (NS) no compite ningún día.
//
// El filtro por día elige con esto QUÉ categorías mostrar, nunca qué atletas: la
// categoría se sigue armando con todos. Best Lifter no tiene división.
export const categoryDays = (
  orderedEntries: ReadonlyArray<Entry>,
  event: Event,
  division?: string,
): number[] => {
  const competing = orderedEntries.filter((entry) => !isEntryNoShow(entry));
  const own = competing.filter(
    (entry) =>
      (entry.events.length === 0 || entry.events[0] === event) &&
      (division === undefined || entry.divisions.length === 0 || entry.divisions[0] === division),
  );
  const pool = own.length > 0 ? own : competing;
  return [...new Set(pool.map((entry) => entry.day))].sort((a, b) => a - b);
};

// Los días en los que efectivamente se levantó en esta categoría. Si son varios,
// conviene aclarar en cada fila cuándo hizo sus kilos el atleta.
export const liftingDaysOf = (orderedEntries: ReadonlyArray<Entry>): number[] => {
  const days = new Set<number>();
  for (const entry of orderedEntries) {
    if (entryHasLifted(entry)) days.add(entry.day);
  }
  return [...days].sort((a, b) => a - b);
};
